<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Surveys</h5>
        <p class="page-sub">Create surveys and collect customer feedback</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click.stop="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="doExport('csv')">CSV</button>
            <button class="export-opt" @click="doExport('pdf')">PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_surveys" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Survey
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search surveys..." />
      <select v-model="filterStatus" @change="page=1; fetchSurveys()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="active">Active</option>
        <option value="closed">Closed</option>
      </select>
      <input v-model="dateFrom" type="date" class="filter-input" style="max-width:160px" />
      <input v-model="dateTo" type="date" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading surveys...</p></div>
      <div v-else-if="!surveys.length" class="empty-state">
        <font-awesome-icon icon="poll" class="empty-icon" />
        <p>No surveys found</p>
        <span>Create a survey to collect feedback from customers</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Title</th><th>Status</th><th>Responses</th><th>Avg Score</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="s in surveys" :key="s.id">
            <td class="name-col">{{ s.name }}</td>
            <td><span :class="['status-badge', `status--${s.status}`]">{{ s.status }}</span></td>
            <td>{{ s.responses_count ?? 0 }}</td>
            <td>{{ s.avg_score ? s.avg_score.toFixed(1) : '—' }}</td>
            <td>{{ fmtDate(s.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button class="btn-link btn-link--responses" @click="openResponses(s)">Responses</button>
                <button v-if="permission?.edit_surveys && s.status === 'active'" class="btn-link btn-link--send" @click="openSend(s)">Send</button>
                <button v-if="permission?.edit_surveys && s.status === 'draft'" class="btn-link btn-link--activate" @click="activateSurvey(s)">Activate</button>
                <button v-if="permission?.edit_surveys && s.status === 'active'" class="btn-link btn-link--close" @click="closeSurvey(s)">Close</button>
                <button v-if="permission?.edit_surveys && s.status !== 'closed'" class="btn-link btn-link--edit" @click="openEdit(s)">Edit</button>
                <button v-if="permission?.delete_surveys && s.status === 'draft'" class="btn-link btn-link--del" @click="confirmDelete(s)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta && meta.last_page > 1" class="pagination">
        <button :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)" class="pg-btn">‹ Prev</button>
        <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)" class="pg-btn">Next ›</button>
      </div>
    </div>

    <!-- ── Responses Drawer ───────────────────────────────────────────── -->
    <div class="drawer-backdrop" :class="{ open: responsesOpen }" @click.self="closeResponses"></div>
    <div class="drawer" :class="{ open: responsesOpen }">
      <div class="drawer-header">
        <div class="drawer-header-left">
          <div class="drawer-icon"><font-awesome-icon icon="chart-bar" /></div>
          <div>
            <p class="drawer-title">Survey Responses</p>
            <p class="drawer-sub">{{ responseSurvey?.name }}</p>
          </div>
        </div>
        <button class="modal-close" @click="closeResponses"><font-awesome-icon icon="times" /></button>
      </div>

      <div class="drawer-body">
        <div v-if="responsesLoading && !responses.length" class="loading-state"><div class="spinner"></div><p>Loading...</p></div>

        <template v-else>
          <!-- Summary cards -->
          <div class="summary-grid">
            <div class="summary-card">
              <p class="summary-val">{{ summary.total_sent ?? 0 }}</p>
              <p class="summary-lbl">Total Sent</p>
            </div>
            <div class="summary-card">
              <p class="summary-val">{{ summary.total_responded ?? 0 }}</p>
              <p class="summary-lbl">Responded</p>
            </div>
            <div class="summary-card">
              <p class="summary-val">{{ summary.response_rate != null ? summary.response_rate + '%' : '—' }}</p>
              <p class="summary-lbl">Response Rate</p>
            </div>
            <div class="summary-card">
              <p class="summary-val">{{ summary.avg_score != null ? summary.avg_score : '—' }}</p>
              <p class="summary-lbl">Avg Score</p>
            </div>
            <div v-if="responseSurvey?.type === 'nps'" class="summary-card summary-card--nps" :class="npsClass">
              <p class="summary-val">{{ summary.nps_score != null ? summary.nps_score : '—' }}</p>
              <p class="summary-lbl">NPS Score</p>
            </div>
          </div>

          <!-- Score distribution -->
          <div v-if="summary.distribution && Object.keys(summary.distribution).length" class="dist-section">
            <p class="dist-title">Score Distribution</p>
            <div class="dist-bars">
              <div v-for="(count, score) in sortedDistribution" :key="score" class="dist-row">
                <span class="dist-score">{{ score }}</span>
                <div class="dist-bar-wrap">
                  <div class="dist-bar" :style="{ width: distPct(count) + '%' }" :class="scoreBarClass(Number(score))"></div>
                </div>
                <span class="dist-count">{{ count }}</span>
              </div>
            </div>
          </div>

          <!-- Responses table -->
          <div class="resp-section">
            <p class="dist-title">Individual Responses <span class="resp-count-badge">{{ summary.total_responded ?? 0 }}</span></p>

            <div v-if="!responses.length && !responsesLoading" class="empty-resp">
              <font-awesome-icon icon="inbox" class="empty-resp-icon" />
              <p>No responses yet</p>
            </div>

            <table v-else class="resp-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Score</th>
                  <th>Comment</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in responses" :key="r.id">
                  <td>
                    <span v-if="r.contact">{{ r.contact.first_name }} {{ r.contact.last_name }}</span>
                    <span v-else-if="r.employee">{{ r.employee.first_name }} {{ r.employee.last_name }}</span>
                    <span v-else-if="r.customer">{{ r.customer.customer_name }}</span>
                    <span v-else class="resp-anon">Anonymous</span>
                  </td>
                  <td>
                    <span class="score-chip" :class="scoreChipClass(r.score, responseSurvey?.type)">{{ r.score }}</span>
                  </td>
                  <td class="resp-comment">{{ r.comment || '—' }}</td>
                  <td class="resp-date">{{ fmtDate(r.responded_at) }}</td>
                </tr>
              </tbody>
            </table>

            <div v-if="responsesMeta && responsesMeta.last_page > 1" class="pagination" style="border-top:none;padding-top:8px">
              <button :disabled="responsesMeta.current_page <= 1" @click="changeResponsesPage(responsesMeta.current_page - 1)" class="pg-btn">‹</button>
              <span class="pg-info">{{ responsesMeta.current_page }} / {{ responsesMeta.last_page }}</span>
              <button :disabled="responsesMeta.current_page >= responsesMeta.last_page" @click="changeResponsesPage(responsesMeta.current_page + 1)" class="pg-btn">›</button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Create / Edit Modal ───────────────────────────────────────────── -->
    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Survey' : 'New Survey' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Survey Name <span class="req">*</span></label>
                <input v-model="form.name" class="form-input" required />
              </div>
              <div class="form-group">
                <label class="form-label">Type</label>
                <select v-model="form.type" class="form-input">
                  <option value="nps">NPS (0–10 scale)</option>
                  <option value="csat">CSAT (1–5 scale)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Question <span class="req">*</span></label>
              <input v-model="form.question" class="form-input" placeholder="e.g. How likely are you to recommend us?" required />
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Description</label>
              <textarea v-model="form.description" class="form-input" rows="2" placeholder="Optional internal note about this survey"></textarea>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Thank You Message</label>
                <input v-model="form.thank_you_message" class="form-input" placeholder="Thanks for your feedback!" />
              </div>
              <div class="form-group">
                <label class="form-label">Expires At</label>
                <input v-model="form.expires_at" type="date" class="form-input" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Survey') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header"><span class="modal-title">Delete Survey</span><button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button></div>
        <div class="modal-body"><p>Delete <strong>{{ deleteTarget?.name }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- Activate Survey Confirm -->
    <div class="modal-overlay" :class="{ open: !!activateTarget }" @click.self="activateTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--green"><font-awesome-icon icon="poll" /></div>
          <div>
            <span class="modal-title">Activate Survey</span>
            <p class="modal-sub">Make this survey available to respondents</p>
          </div>
          <button class="modal-close" @click="activateTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <p class="action-name">{{ activateTarget?.name }}</p>
          </div>
          <p class="action-note">Once active, the survey can receive responses. You can close it at any time.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="activateTarget = null">Cancel</button>
          <button class="btn-activate" @click="doActivate" :disabled="submitting">
            <font-awesome-icon icon="poll" class="me-1" />
            {{ submitting ? 'Activating…' : 'Activate Survey' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Send Survey Modal -->
    <div class="modal-overlay" :class="{ open: !!sendTarget }" @click.self="closeSendModal">
      <div class="modal-box modal-box--send">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--blue"><font-awesome-icon icon="paper-plane" /></div>
          <div>
            <span class="modal-title">Send Survey</span>
            <p class="modal-sub">{{ sendTarget?.name }}</p>
          </div>
          <button class="modal-close" @click="closeSendModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body modal-body--send">

          <!-- Audience tabs: Contacts / Employees -->
          <div class="audience-tabs">
            <button class="audience-tab" :class="{ active: audienceType === 'contacts' }" @click="audienceType = 'contacts'">
              <font-awesome-icon icon="address-book" class="me-1" /> CRM Contacts
            </button>
            <button class="audience-tab" :class="{ active: audienceType === 'employees' }" @click="audienceType = 'employees'">
              <font-awesome-icon icon="id-badge" class="me-1" /> Employees
            </button>
          </div>

          <!-- ── Contacts audience ── -->
          <template v-if="audienceType === 'contacts'">
            <div class="send-mode-row" style="margin-top:14px">
              <button class="send-mode-btn" :class="{ 'send-mode-btn--active': sendMode === 'selected' }" @click="sendMode = 'selected'">
                <font-awesome-icon icon="user-check" class="me-1" /> Selected contacts
              </button>
              <button class="send-mode-btn" :class="{ 'send-mode-btn--active': sendMode === 'all' }" @click="sendMode = 'all'; sendContactIds = []">
                <font-awesome-icon icon="users" class="me-1" /> All contacts
                <span class="send-mode-count">{{ contacts.length }}</span>
              </button>
            </div>

            <div v-if="sendMode === 'selected'" class="form-group form-group--full" style="margin-top:14px">
              <label class="form-label">Search and select contacts</label>
              <Multiselect
                v-model="sendContactIds"
                :options="contactOptions"
                mode="multiple"
                :searchable="true"
                :close-on-select="false"
                :filter-results="true"
                placeholder="Search by name or company..."
                value-prop="value"
                label="label"
                class="ms-contact"
              />
              <p v-if="sendContactIds.length" class="send-sel-count">{{ sendContactIds.length }} contact{{ sendContactIds.length === 1 ? '' : 's' }} selected</p>
            </div>
            <div v-else class="send-all-info" style="margin-top:14px">
              <font-awesome-icon icon="users" class="send-all-icon" />
              <div>
                <p class="send-all-label">Send to all {{ contacts.length }} contacts</p>
                <p class="send-all-sub">Contacts who have already responded will be skipped automatically.</p>
              </div>
            </div>
          </template>

          <!-- ── Employees audience ── -->
          <template v-else>
            <div class="emp-target-grid" style="margin-top:14px">
              <button class="emp-target-btn" :class="{ active: empTarget === 'all' }" @click="empTarget = 'all'; clearEmpFields()">
                <font-awesome-icon icon="users" />
                <span>All Employees</span>
              </button>
              <button class="emp-target-btn" :class="{ active: empTarget === 'individual' }" @click="empTarget = 'individual'; clearEmpFields()">
                <font-awesome-icon icon="user" />
                <span>Individual</span>
              </button>
              <button class="emp-target-btn" :class="{ active: empTarget === 'department' }" @click="empTarget = 'department'; clearEmpFields()">
                <font-awesome-icon icon="building" />
                <span>Department</span>
              </button>
              <button class="emp-target-btn" :class="{ active: empTarget === 'unit' }" @click="empTarget = 'unit'; clearEmpFields()">
                <font-awesome-icon icon="sitemap" />
                <span>Unit</span>
              </button>
            </div>

            <!-- Individual employee typeahead -->
            <div v-if="empTarget === 'individual'" class="form-group form-group--full" style="margin-top:14px">
              <label class="form-label">Search Employee</label>
              <div class="typeahead-wrap">
                <div v-if="empSelected" class="individual-selected">
                  <span class="individual-name">{{ empSelected.first_name }} {{ empSelected.last_name }}</span>
                  <span class="individual-sub">{{ empSelected.email }}</span>
                  <button type="button" class="individual-clear" @click="empSelected = null; empSearch = ''">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
                <template v-else>
                  <input
                    v-model="empSearch"
                    class="form-input"
                    placeholder="Type name or email..."
                    @input="debouncedEmpSearch"
                    autocomplete="off"
                  />
                  <div v-if="empResults.length" class="typeahead-dropdown">
                    <div v-for="r in empResults" :key="r.id" class="typeahead-item" @mousedown.prevent="empSelected = r; empSearch = ''">
                      <span class="ta-name">{{ r.first_name }} {{ r.last_name }}</span>
                      <span class="ta-sub">{{ r.email }}</span>
                    </div>
                  </div>
                  <div v-else-if="empSearching" class="typeahead-dropdown typeahead-loading">Searching…</div>
                </template>
              </div>
            </div>

            <!-- All employees info -->
            <div v-if="empTarget === 'all'" class="send-all-info" style="margin-top:14px">
              <font-awesome-icon icon="users" class="send-all-icon" />
              <div>
                <p class="send-all-label">Send to all active employees</p>
                <p class="send-all-sub">Employees who have already responded will be skipped automatically.</p>
              </div>
            </div>

            <!-- Department picker -->
            <div v-if="empTarget === 'department'" class="form-group form-group--full" style="margin-top:14px">
              <label class="form-label">Select Department</label>
              <select v-model="empDepartmentId" class="form-input">
                <option value="">Choose department...</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>

            <!-- Unit picker -->
            <div v-if="empTarget === 'unit'" class="form-group form-group--full" style="margin-top:14px">
              <label class="form-label">Select Unit</label>
              <select v-model="empUnitId" class="form-input">
                <option value="">Choose unit...</option>
                <option v-for="u in units" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
          </template>

          <div v-if="sendResult" class="send-result" :class="{ 'send-result--ok': sendResult.ok }" style="margin-top:14px">
            {{ sendResult.msg }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeSendModal">Cancel</button>
          <button class="btn-send" :disabled="sendDisabled || submitting" @click="doSend">
            <font-awesome-icon icon="paper-plane" class="me-1" />
            {{ submitting ? 'Sending…' : 'Send Survey' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Close Survey Confirm -->
    <div class="modal-overlay" :class="{ open: !!closeTarget }" @click.self="closeTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--grey"><font-awesome-icon icon="times-circle" /></div>
          <div>
            <span class="modal-title">Close Survey</span>
            <p class="modal-sub">Stop accepting new responses</p>
          </div>
          <button class="modal-close" @click="closeTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <p class="action-name">{{ closeTarget?.name }}</p>
          </div>
          <p class="action-note">No new responses will be accepted. Existing responses are preserved and you can still view results.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeTarget = null">Cancel</button>
          <button class="btn-close-survey" @click="doClose" :disabled="submitting">
            {{ submitting ? 'Closing…' : 'Close Survey' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import Multiselect from '@vueform/multiselect';
export default {
  components: { Multiselect },
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    contactOptions() {
      return this.contacts.map(c => ({
        value: c.id,
        label: `${c.full_name || (c.first_name + ' ' + c.last_name).trim()}${c.company_name ? ' — ' + c.company_name : ''}`,
      }));
    },
    sendDisabled() {
      if (this.audienceType === 'contacts') {
        return this.sendMode === 'selected' && !this.sendContactIds.length;
      }
      // employees
      if (this.empTarget === 'all') return false;
      if (this.empTarget === 'individual') return !this.empSelected;
      if (this.empTarget === 'department') return !this.empDepartmentId;
      if (this.empTarget === 'unit') return !this.empUnitId;
      return true;
    },
    sortedDistribution() {
      if (!this.summary.distribution) return {};
      return Object.fromEntries(
        Object.entries(this.summary.distribution).sort((a, b) => Number(a[0]) - Number(b[0]))
      );
    },
    npsClass() {
      const s = this.summary.nps_score;
      if (s == null) return '';
      if (s >= 50) return 'nps--great';
      if (s >= 0)  return 'nps--ok';
      return 'nps--bad';
    },
  },
  data() {
    return {
      surveys: [], meta: null, loading: false, submitting: false,
      search: '', filterStatus: '', page: 1,
      modalOpen: false, editing: null, deleteTarget: null, activateTarget: null, closeTarget: null,
      form: { name:'', type:'nps', question:'', description:'', thank_you_message:'', expires_at:'' },
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      // send
      sendTarget: null,
      audienceType: 'contacts',          // 'contacts' | 'employees'
      sendMode: 'selected',              // contacts: 'selected' | 'all'
      sendContactIds: [], sendResult: null, contacts: [],
      // employee targeting
      empTarget: 'all',                  // 'all' | 'individual' | 'department' | 'unit'
      empSelected: null,
      empSearch: '', empResults: [], empSearching: false, empSearchTimer: null,
      empDepartmentId: '', empUnitId: '',
      departments: [], units: [],
      // responses drawer
      responsesOpen: false, responseSurvey: null,
      summary: {}, responses: [], responsesMeta: null,
      responsesLoading: false, responsesPage: 1,
      debounceTimer: null,
    };
  },
  mounted() { this.fetchSurveys(); this.fetchContacts(); this.fetchDepartments(); this.fetchUnits(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchSurveys() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/surveys', { params: { search: this.search, status: this.filterStatus, page: this.page, from: this.dateFrom || undefined, to: this.dateTo || undefined } });
        this.surveys = data.data?.data ?? data.data ?? data ?? [];
        this.meta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load surveys'); }
      finally { this.loading = false; }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchSurveys(); }, 400); },
    changePage(p) { this.page = p; this.fetchSurveys(); },
    applyDateFilter() { this.page = 1; this.fetchSurveys(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchSurveys(); },
    async doExport(format) {
      this.exportMenuOpen = false;
      this.exporting = true;
      try {
        const params = { format };
        if (this.search) params.search = this.search;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.dateFrom) params.from = this.dateFrom;
        if (this.dateTo) params.to = this.dateTo;
        const { data } = await axios.get('/crm/surveys/export', { params, responseType: 'blob' });
        const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
        const ext  = format === 'pdf' ? 'pdf' : 'csv';
        const ts   = new Date().toISOString().slice(0, 10);
        const url  = URL.createObjectURL(new Blob([data], { type: mime }));
        const a    = document.createElement('a');
        a.href = url; a.download = `surveys_${ts}.${ext}`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    openCreate() { this.editing = null; this.form = { name:'', type:'nps', question:'', description:'', thank_you_message:'', expires_at:'' }; this.modalOpen = true; },
    openEdit(s) { this.editing = s.id; this.form = { name: s.name, type: s.type??'nps', question: s.question??'', description: s.description??'', thank_you_message: s.thank_you_message??'', expires_at: s.expires_at??'' }; this.modalOpen = true; },
    closeModal() { this.modalOpen = false; },
    confirmDelete(s) { this.deleteTarget = s; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) { await axios.put(`/crm/surveys/${this.editing}`, this.form); this.toast.success('Survey updated'); }
        else              { await axios.post('/crm/surveys', this.form); this.toast.success('Survey created'); }
        this.closeModal(); this.fetchSurveys();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    async doDelete() {
      this.submitting = true;
      try { await axios.delete(`/crm/surveys/${this.deleteTarget.id}`); this.toast.success('Deleted'); this.deleteTarget = null; this.fetchSurveys(); }
      catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },
    async fetchContacts() {
      try {
        const { data } = await axios.get('/crm/contacts', { params: { per_page: 500 } });
        this.contacts = data.data?.data ?? data.data ?? [];
      } catch { /* silent */ }
    },
    openSend(s) {
      this.sendTarget     = s;
      this.audienceType   = 'contacts';
      this.sendMode       = 'selected';
      this.sendContactIds = [];
      this.sendResult     = null;
      this.empTarget      = 'all';
      this.empSelected    = null;
      this.empSearch      = '';
      this.empResults     = [];
      this.empDepartmentId = '';
      this.empUnitId      = '';
    },
    closeSendModal() { this.sendTarget = null; this.sendResult = null; },

    clearEmpFields() {
      this.empSelected     = null;
      this.empSearch       = '';
      this.empResults      = [];
      this.empDepartmentId = '';
      this.empUnitId       = '';
    },

    debouncedEmpSearch() {
      clearTimeout(this.empSearchTimer);
      if (!this.empSearch.trim()) { this.empResults = []; return; }
      this.empSearching = true;
      this.empSearchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/payroll/employees', { params: { search: this.empSearch, per_page: 10 } });
          this.empResults = data.data?.data ?? data.data ?? [];
        } catch { this.empResults = []; }
        finally { this.empSearching = false; }
      }, 300);
    },

    async fetchDepartments() {
      try {
        const { data } = await axios.get('/payroll/department/active');
        this.departments = data.data ?? data ?? [];
      } catch { /* silent */ }
    },
    async fetchUnits() {
      try {
        const { data } = await axios.get('/payroll/unit/active');
        this.units = data.data ?? data ?? [];
      } catch { /* silent */ }
    },

    async doSend() {
      this.submitting = true;
      try {
        let payload = {};

        if (this.audienceType === 'contacts') {
          payload = this.sendMode === 'all'
            ? { send_to_all: true }
            : { contact_ids: this.sendContactIds };
        } else {
          if (this.empTarget === 'all')        payload = { send_to_all_employees: true };
          else if (this.empTarget === 'individual') payload = { employee_ids: [this.empSelected.id] };
          else if (this.empTarget === 'department') payload = { department_id: this.empDepartmentId };
          else if (this.empTarget === 'unit')       payload = { unit_id: this.empUnitId };
        }

        const { data } = await axios.post(`/crm/surveys/${this.sendTarget.id}/send`, payload);
        if (data.status) {
          this.sendResult = { ok: true, msg: data.message };
          this.toast.success(data.message);
          this.fetchSurveys();
        } else {
          this.sendResult = { ok: false, msg: data.message };
        }
      } catch (e) { this.sendResult = { ok: false, msg: e.response?.data?.message ?? 'Failed to send' }; }
      finally { this.submitting = false; }
    },
    activateSurvey(s) { this.activateTarget = s; },
    async doActivate() {
      this.submitting = true;
      try {
        await axios.post(`/crm/surveys/${this.activateTarget.id}/activate`);
        this.toast.success('Survey activated');
        this.activateTarget = null;
        this.fetchSurveys();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to activate'); }
      finally { this.submitting = false; }
    },
    closeSurvey(s) { this.closeTarget = s; },
    async doClose() {
      this.submitting = true;
      try {
        await axios.post(`/crm/surveys/${this.closeTarget.id}/close`);
        this.toast.success('Survey closed');
        this.closeTarget = null;
        this.fetchSurveys();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to close'); }
      finally { this.submitting = false; }
    },

    // ── Responses drawer ──────────────────────────────────────────────
    async openResponses(s) {
      this.responseSurvey  = s;
      this.responsesOpen   = true;
      this.responsesPage   = 1;
      this.summary         = {};
      this.responses       = [];
      this.responsesMeta   = null;
      this.responsesLoading = true;
      await Promise.all([this.loadSummary(s.id), this.loadResponses(s.id, 1)]);
      this.responsesLoading = false;
    },
    closeResponses() { this.responsesOpen = false; },
    async loadSummary(id) {
      try {
        const { data } = await axios.get(`/crm/surveys/${id}/summary`);
        this.summary = data.data ?? {};
      } catch { /* silent */ }
    },
    async loadResponses(id, page) {
      try {
        const { data } = await axios.get(`/crm/surveys/${id}/responses`, { params: { page, per_page: 15 } });
        this.responses     = data.data?.data ?? data.data ?? [];
        this.responsesMeta = data.data?.meta ?? data.meta ?? null;
      } catch { /* silent */ }
    },
    async changeResponsesPage(p) {
      this.responsesPage = p;
      this.responsesLoading = true;
      await this.loadResponses(this.responseSurvey.id, p);
      this.responsesLoading = false;
    },

    // ── Helpers ───────────────────────────────────────────────────────
    distPct(count) {
      const max = Math.max(...Object.values(this.summary.distribution ?? {}), 1);
      return Math.round((count / max) * 100);
    },
    scoreBarClass(score) {
      const type = this.responseSurvey?.type;
      if (type === 'nps') {
        if (score <= 6) return 'bar--red';
        if (score <= 8) return 'bar--amber';
        return 'bar--green';
      }
      if (type === 'csat') {
        if (score <= 2) return 'bar--red';
        if (score === 3) return 'bar--amber';
        return 'bar--green';
      }
      return 'bar--blue';
    },
    scoreChipClass(score, type) {
      if (score == null) return 'chip--grey';
      if (type === 'nps') {
        if (score <= 6) return 'chip--red';
        if (score <= 8) return 'chip--amber';
        return 'chip--green';
      }
      if (type === 'csat') {
        if (score <= 2) return 'chip--red';
        if (score === 3) return 'chip--amber';
        return 'chip--green';
      }
      return 'chip--blue';
    },
    fmtDate(v) { return v ? new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'; },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:180px;outline:none}
.filter-input:focus{border-color:#004AAD}
.filter-select{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--activate{color:#166534}
.btn-link--close{color:#92400e}
.btn-link--del{color:#e03535}
.btn-link--responses{color:#0891b2}
.status-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.status--draft{background:#f1f5ff;color:#004AAD}
.status--active{background:#dcfce7;color:#166534}
.status--closed{background:#f3f4f6;color:#6b7280}
.pagination{display:flex;align-items:center;justify-content:center;gap:12px;padding:14px;border-top:1px solid #f0f4fa}
.pg-btn{padding:6px 14px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-info{font-size:13px;color:#7a90b0}
/* Modals */
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:520px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
.modal-box--send{overflow:visible}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.modal-header-icon{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.modal-header-icon--green{background:#dcfce7;color:#166534}
.modal-header-icon--grey{background:#f3f4f6;color:#6b7280}
.modal-sub{font-size:12px;color:#7a90b0;margin:2px 0 0}
.action-card{background:#f4f7fb;border-radius:10px;padding:14px 16px;margin-bottom:14px}
.action-name{font-size:14px;font-weight:700;color:#1a2b45;margin:0 0 4px}
.action-note{font-size:13px;color:#5a6a85;margin:0}
.btn-activate{background:#166534;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-activate:hover{background:#14532d}
.btn-activate:disabled{opacity:.55;cursor:not-allowed}
.btn-close-survey{background:#6b7280;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-close-survey:hover{background:#4b5563}
.btn-close-survey:disabled{opacity:.55;cursor:not-allowed}
.btn-link--send{color:#7c3aed}
.modal-header-icon--blue{background:#e8f0fb;color:#004AAD}
.btn-send{background:#7c3aed;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-send:hover{background:#6d28d9}
.btn-send:disabled{opacity:.55;cursor:not-allowed}
.send-result{font-size:12.5px;color:#e03535;margin-top:14px;padding:10px 13px;background:#fff5f5;border-radius:8px;border:1px solid #fecaca}
.send-result--ok{color:#166534;background:#f0fdf4;border-color:#bbf7d0}
.send-mode-row{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.send-mode-btn{background:#f4f7fb;border:1.5px solid #e8edf5;border-radius:9px;padding:10px 14px;font-size:13px;font-weight:600;color:#5a6a85;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all .15s;justify-content:center}
.send-mode-btn:hover{border-color:#7c3aed;color:#7c3aed}
.send-mode-btn--active{background:#f5f3ff;border-color:#7c3aed;color:#7c3aed}
.send-mode-count{background:#7c3aed;color:#fff;border-radius:20px;padding:1px 8px;font-size:11px;margin-left:4px}
.send-sel-count{font-size:12px;color:#7c3aed;margin:6px 0 0;font-weight:600}
.send-all-info{display:flex;align-items:center;gap:14px;background:#f5f3ff;border:1.5px solid #ede9fe;border-radius:10px;padding:14px 16px;margin-top:14px}
.send-all-icon{font-size:24px;color:#7c3aed;flex-shrink:0}
.send-all-label{font-size:14px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.send-all-sub{font-size:12px;color:#7a90b0;margin:0}
/* Send modal body scroll */
.modal-body--send{max-height:calc(90vh - 140px);overflow-y:auto}
/* Audience tabs */
.audience-tabs{display:flex;gap:0;border:1.5px solid #e4eaf2;border-radius:10px;overflow:hidden}
.audience-tab{flex:1;padding:10px 14px;font-size:13px;font-weight:600;color:#5a6a85;background:#f4f7fb;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;transition:background .15s,color .15s}
.audience-tab:not(:last-child){border-right:1.5px solid #e4eaf2}
.audience-tab.active{background:#7c3aed;color:#fff}
/* Employee target grid */
.emp-target-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
.emp-target-btn{display:flex;flex-direction:column;align-items:center;gap:5px;padding:12px 8px;border:1.5px solid #e4eaf2;border-radius:10px;background:#f4f7fb;font-size:12px;font-weight:600;color:#5a6a85;cursor:pointer;transition:all .15s}
.emp-target-btn:hover{border-color:#7c3aed;color:#7c3aed;background:#f5f3ff}
.emp-target-btn.active{border-color:#7c3aed;background:#f5f3ff;color:#7c3aed}
.emp-target-btn svg{font-size:18px}
/* Typeahead (shared with campaign, scoped here too) */
.typeahead-wrap{position:relative}
.typeahead-dropdown{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1.5px solid #dde5f0;border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,.1);z-index:200;max-height:200px;overflow-y:auto}
.typeahead-item{display:flex;justify-content:space-between;align-items:center;padding:9px 13px;cursor:pointer;font-size:13px;gap:10px}
.typeahead-item:hover{background:#f5f3ff}
.ta-name{font-weight:600;color:#1a2b45}
.ta-sub{font-size:12px;color:#7a90b0;text-align:right;white-space:nowrap}
.typeahead-loading{padding:10px 14px;font-size:13px;color:#7a90b0;font-style:italic}
.individual-selected{display:flex;align-items:center;gap:8px;background:#f5f3ff;border:1.5px solid #c4b5fd;border-radius:8px;padding:8px 12px}
.individual-name{flex:1;font-size:13px;font-weight:600;color:#1a2b45}
.individual-sub{font-size:12px;color:#7a90b0}
.individual-clear{background:none;border:none;color:#7a90b0;cursor:pointer;font-size:13px;padding:2px 4px;border-radius:4px}
.individual-clear:hover{color:#e03535}

/* ── Responses drawer ───────────────────────────────────────────────── */
.drawer-backdrop{position:fixed;inset:0;z-index:1040;background:rgba(15,25,50,.35);opacity:0;pointer-events:none;transition:opacity .25s}
.drawer-backdrop.open{opacity:1;pointer-events:all}
.drawer{position:fixed;top:0;right:0;bottom:0;z-index:1041;width:520px;max-width:95vw;background:#fff;box-shadow:-8px 0 40px rgba(0,0,0,.12);transform:translateX(100%);transition:transform .25s ease;display:flex;flex-direction:column}
.drawer.open{transform:translateX(0)}
.drawer-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2;flex-shrink:0}
.drawer-header-left{display:flex;align-items:center;gap:12px}
.drawer-icon{width:36px;height:36px;border-radius:9px;background:#e0f2fe;color:#0891b2;display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0}
.drawer-title{font-size:15px;font-weight:700;color:#1a2b45;margin:0 0 2px}
.drawer-sub{font-size:12px;color:#7a90b0;margin:0}
.drawer-body{flex:1;overflow-y:auto;padding:20px 22px}

/* summary cards */
.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px}
.summary-card{background:#f4f7fb;border-radius:10px;padding:12px 14px;text-align:center}
.summary-card--nps{grid-column:1/-1}
.summary-val{font-size:22px;font-weight:800;color:#1a2b45;margin:0 0 2px}
.summary-lbl{font-size:11px;color:#7a90b0;font-weight:600;text-transform:uppercase;letter-spacing:.04em;margin:0}
.nps--great .summary-val{color:#166534}
.nps--ok .summary-val{color:#d97706}
.nps--bad .summary-val{color:#dc2626}

/* distribution */
.dist-section{margin-bottom:20px}
.dist-title{font-size:12.5px;font-weight:700;color:#1a2b45;text-transform:uppercase;letter-spacing:.04em;margin:0 0 10px;display:flex;align-items:center;gap:8px}
.dist-bars{display:flex;flex-direction:column;gap:5px}
.dist-row{display:grid;grid-template-columns:28px 1fr 28px;align-items:center;gap:8px}
.dist-score{font-size:12px;font-weight:700;color:#5a6a85;text-align:right}
.dist-count{font-size:12px;color:#7a90b0;text-align:left}
.dist-bar-wrap{height:14px;background:#f0f4fa;border-radius:99px;overflow:hidden}
.dist-bar{height:100%;border-radius:99px;min-width:4px;transition:width .4s ease}
.bar--red{background:#fca5a5}
.bar--amber{background:#fcd34d}
.bar--green{background:#6ee7b7}
.bar--blue{background:#93c5fd}

/* individual responses */
.resp-section{margin-top:4px}
.resp-count-badge{background:#e0f2fe;color:#0891b2;border-radius:20px;padding:1px 8px;font-size:11px;font-weight:700;letter-spacing:0;text-transform:none}
.empty-resp{display:flex;flex-direction:column;align-items:center;padding:32px 0;color:#a0aec0;gap:8px}
.empty-resp-icon{font-size:28px;opacity:.5}
.empty-resp p{margin:0;font-size:13px}
.resp-table{width:100%;border-collapse:collapse;font-size:13px;margin-top:10px}
.resp-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:9px 12px;text-align:left;font-size:12px;white-space:nowrap}
.resp-table td{padding:10px 12px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:top}
.resp-table tbody tr:hover{background:#f9fbff}
.resp-comment{color:#5a6a85;max-width:160px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.resp-date{color:#7a90b0;white-space:nowrap;font-size:12px}
.resp-anon{color:#a0aec0;font-style:italic}
.score-chip{display:inline-block;font-size:12px;font-weight:800;border-radius:7px;padding:2px 9px;min-width:28px;text-align:center}
.chip--green{background:#dcfce7;color:#166534}
.chip--amber{background:#fef9c3;color:#92400e}
.chip--red{background:#fee2e2;color:#dc2626}
.chip--blue{background:#dbeafe;color:#1d4ed8}
.chip--grey{background:#f3f4f6;color:#6b7280}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#fff;color:#1a2b45;border:1.5px solid #dde5f0;padding:8px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-export:hover{border-color:#004AAD;color:#004AAD}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;right:0;top:calc(100% + 4px);background:#fff;border:1.5px solid #e4eaf2;border-radius:9px;box-shadow:0 8px 28px rgba(0,0,0,.1);z-index:200;min-width:140px;padding:5px 0}
.export-opt{display:block;width:100%;background:none;border:none;padding:9px 16px;text-align:left;font-size:13px;color:#1a2b45;cursor:pointer}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:9px 15px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
</style>
<style src="@vueform/multiselect/themes/default.css"></style>
<style>
.ms-contact {
  --ms-border-color: #dde5f0;
  --ms-border-width: 1.5px;
  --ms-radius: 8px;
  --ms-font-size: 13px;
  --ms-py: 0.45rem;
  --ms-px: 0.75rem;
  --ms-ring-color: rgba(0,74,173,.07);
  --ms-ring-width: 3px;
  --ms-option-bg-selected: #7c3aed;
  --ms-option-bg-selected-pointed: #6d28d9;
  --ms-option-color-selected: #fff;
  --ms-dropdown-border-color: #dde5f0;
  --ms-dropdown-radius: 8px;
}
</style>
